// @flow
import * as React from 'react';
import Modal from 'react-modal';
import { capitalize } from 'lodash';

import {
  setAutoMessageStatus,
  duplicateAutoMessage,
  deleteAutoMessage,
} from 'admin/actions/AutoMessageActions';
import { getAutoMessagesLink, getUploadedFilesLink } from './lib/linkHelpers';
import AdminAutoMessageDetailPreviousSendsTable from './AdminAutoMessageDetailPreviousSendsTable';
import AdminAutoMessageDetailUnsentFilesTable from './AdminAutoMessageDetailUnsentFilesTable';
import AdminAutoMessageDetailViewActions from './AdminAutoMessageDetailViewActions';
import { AdminAutoMessagesDeleteConfirmDialog } from 'admin/components/AdminAutoMessages';
import AdminNewAutoMessageModal from 'admin/components/AdminNewAutoMessage';
import AdminNoticeBanner from 'admin/components/AdminNoticeBanner';
import Divider from 'components/Divider';
import Heading from 'components/Heading';
import Glyph from 'components/Glyph';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
import TextBadge from 'components/TextBadge';
import ToastActions from 'actions/ToastActions';
import VisibilityWatch from 'components/VisibilityWatch';

import type { GetAutoMessageByUuid_autoMessaging_autoMessages_autoMessages } from 'admin/components/AdminAutoMessages/queries/__generated__/GetAutoMessageByUuid';

type Props = {
  viewingOrgUuid: string,
  autoMessage: GetAutoMessageByUuid_autoMessaging_autoMessages_autoMessages,
  renderLoading: ?React.Element,
  renderPaginatedLoading: ?React.Element,
  renderError: ?React.Element,
  onRedirectToMessage: (messageUuid: string) => void,
  onRedirectToList: () => void,
  urlSlug: string,
  loadNextPage: () => void,
};

type State = {
  isNewAutoMessageDialogOpened: boolean,
  isConfirmDeleteDialogOpened: boolean,
};

class AdminAutoMessageDetailView extends React.Component<Props, State> {
  state = {
    isNewAutoMessageDialogOpened: false,
    isConfirmDeleteDialogOpened: false,
  };

  toggleConfirmDeleteDialog = () => {
    this.setState({ isConfirmDeleteDialogOpened: !this.state.isConfirmDeleteDialogOpened });
  };

  toggleNewAutoMessageDialog = () => {
    this.setState({ isNewAutoMessageDialogOpened: !this.state.isNewAutoMessageDialogOpened });
  };

  handleDelete = () => {
    const { autoMessage } = this.props;
    if (autoMessage.uuid) {
      deleteAutoMessage(
        autoMessage.uuid,
        autoMessage.messageName,
        this.props.viewingOrgUuid,
        this.props.onRedirectToList,
        this.toggleConfirmDeleteDialog
      );
      return;
    }
    console.error('AdminAutoMessagesView - tried to delete an auto message without a uuid');
    ToastActions.showErrorToast('Oops! Looks like something went wrong.');
  };

  renderNotice = (
    status: 'draft' | 'inactive',
    csvName: string,
    unsentFileUploads?: $ReadOnlyArray<*>
  ) => {
    const copyWithoutReceivedFiles = (
      <Text size="small" lineHeight="large">
        Once {csvName || 'a CSV file'} is transferred to Remind, a preview of message details and
        errors will appear here.
      </Text>
    );

    return (
      <Row>
        <AdminNoticeBanner
          alwaysShowDetails
          actionElement={undefined}
          color="mascot"
          details={
            unsentFileUploads && unsentFileUploads.length ? (
              <AdminAutoMessageDetailUnsentFilesTable
                csvName={csvName}
                unsentFileUploads={unsentFileUploads}
              />
            ) : (
              copyWithoutReceivedFiles
            )
          }>
          <Heading level="3" flush>
            This auto message is not active
          </Heading>
        </AdminNoticeBanner>
      </Row>
    );
  };

  render() {
    const {
      renderLoading,
      renderPaginatedLoading,
      renderError,
      autoMessage,
      urlSlug,
      viewingOrgUuid,
      onRedirectToMessage,
    } = this.props;
    if (renderError) {
      return renderError;
    }

    if (renderLoading) {
      return renderLoading;
    }

    const {
      unsentFileUploads,
      status,
      messageName,
      isActivatable,
      previousSends,
      csvName,
      creatorOrganization,
      uuid,
    } = autoMessage;

    const statusToColor = {
      active: 'atlas',
      inactive: 'pencil',
      draft: 'graphite',
    };

    const messageIsActive = status === 'active';
    const canEdit = creatorOrganization ? creatorOrganization.isAdmin : false;
    const creatorOrgName = creatorOrganization ? creatorOrganization.name : undefined;

    return (
      <React.Fragment>
        <Modal isOpen={this.state.isNewAutoMessageDialogOpened}>
          <AdminNewAutoMessageModal
            onBack={this.toggleNewAutoMessageDialog}
            viewingOrgUuid={viewingOrgUuid}
            messageUuid={uuid}
          />
        </Modal>
        <Modal isOpen={this.state.isConfirmDeleteDialogOpened}>
          <AdminAutoMessagesDeleteConfirmDialog
            messageName={messageName}
            onConfirm={this.handleDelete}
            onRequestHide={this.toggleConfirmDeleteDialog}
          />
        </Modal>
        {!canEdit && (
          <React.Fragment>
            <Text color="graphite" size="xsmall" italic>
              Only an administrator
              {creatorOrgName ? ` from ${creatorOrgName}` : ' at the district level'} can edit this
              auto message.
            </Text>
            <Divider size="medium" />
          </React.Fragment>
        )}
        <Row size="small">
          <Heading level="2">
            <MediaObject figure="right" center>
              <MediaObject center gutter="small">
                <MediaObject center gutter="small">
                  <Link to={getAutoMessagesLink(urlSlug)}>Auto messages</Link>
                  <Glyph size="medium" name="chevron-right" />
                </MediaObject>
                <MediaObject center gutter="small" figure="right" inline>
                  <Text truncate>{messageName}</Text>
                  <TextBadge
                    textSize="xxxxsmall"
                    textColor="whiteout"
                    color={statusToColor[status]}>
                    {capitalize(status)}
                  </TextBadge>
                </MediaObject>
              </MediaObject>
              <AdminAutoMessageDetailViewActions
                messageIsActive={messageIsActive}
                creatorOrgName={creatorOrgName}
                canEdit={canEdit}
                isActivatable={isActivatable}
                onEdit={this.toggleNewAutoMessageDialog}
                onDuplicate={() =>
                  duplicateAutoMessage(autoMessage, viewingOrgUuid, onRedirectToMessage)
                }
                onChangeStatus={() =>
                  setAutoMessageStatus(
                    autoMessage.uuid,
                    messageIsActive ? 'inactive' : 'active',
                    viewingOrgUuid,
                    autoMessage.messageName
                  )
                }
                onDelete={this.toggleConfirmDeleteDialog}
              />
            </MediaObject>
          </Heading>
        </Row>
        {status !== 'active' && this.renderNotice(status, csvName, unsentFileUploads)}
        <AdminAutoMessageDetailPreviousSendsTable
          previousSends={previousSends}
          fileUploadsUrlSlug={getUploadedFilesLink(urlSlug)}
        />
        <VisibilityWatch interval={500} whileVisible={this.props.loadNextPage} />
        {renderPaginatedLoading}
      </React.Fragment>
    );
  }
}

export default AdminAutoMessageDetailView;
